"use client";

import clsx from "clsx";
import Link from "next/link";
import { getSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import type { Session } from "next-auth";
import type { CSSProperties } from "react";
import { useMemo, useState, useEffect, useRef } from "react";
import { ChevronDown, X } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@radix-ui/react-collapsible";

import type { Plan } from "@/types";
import { useDelayedToggleState, useOutOfBoundsHitToggle } from "@/lib/hooks";
import { cn, getInitials, getAvatarFallbackClassName, isUserAdmin, isUserPremium, toHeaderCase } from "@/lib/utils";
import { APPLE_APP_STORE_LINK, GOOGLE_APP_STORE_LINK } from "@/lib/constant";
import {
	MAX_SAVED_LOGIN_ACCOUNTS,
	canUseAccountSwitcher,
	getSavedAccountDisplayName,
	getSavedLoginAccounts,
	isSavedLoginAccountSignedOut,
	markSavedLoginAccountSignedOut,
	moveSavedAccountToTop,
	removeSavedLoginAccount,
	saveCurrentAccountIfEligible,
} from "@/lib/auth/saved-accounts";
import type { SavedLoginAccount } from "@/lib/auth/saved-accounts";
import AccountSwitcherAccountRow from "./AccountSwitcherAccountRow";
import { invalidateApiSessionCache } from "@/app/api";

// Import Avatar components directly instead of dynamic import to avoid webpack issues
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import MobileAppButton from "@/components/cards/MobileAppButton";
import MenuIcon from "@/components/icons/MenuIcon";
import AuthDialog from "@/components/auth/AuthDialog";
import AccountSwitcherTwoFactorDialog from "@/components/auth/AccountSwitcherTwoFactorDialog";
import TrackedCtaLink from "@/components/analytics/TrackedCtaLink";
import { TrackedNativeButton } from "@/components/analytics/TrackedCtaButton";
import { useToast } from "@/components/ui/use-toast";
import {
	executeAccountSwitch,
	parkSessionForAccountSwitcher,
	type AccountSwitchResult,
} from "@/lib/auth/switch-account";

import AppleIconLight from "@/../public/assets/home/downloadApp/apple.svg";
import AppleIconDark from "@/../public/assets/home/downloadApp/apple-green.svg";
import GoogleIconLight from "@/../public/assets/home/downloadApp/google.svg";
import GoogleIconDark from "@/../public/assets/home/downloadApp/google-green.svg";

type Props = { session: Session | null; plan: Plan | null };
const ACCOUNT_SWITCHER_LONG_PRESS_MS = 520;

function getCurrentReturnUrl() {
	if (typeof window === "undefined") return "/";
	return `${window.location.pathname}${window.location.search}${window.location.hash}` || "/";
}

function reloadCurrentPage() {
	if (typeof window === "undefined") return;
	window.history.replaceState(null, "", getCurrentReturnUrl());
	window.location.reload();
}

export default function NavMobileMenuButton({ session, plan }: Props) {
	const pathname = usePathname();
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [clicked, showMenu, setMenuVisibility] = useDelayedToggleState(false, 300);
	const [mounted, setMounted] = useState(false);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [loginReturnUrl, setLoginReturnUrl] = useState("/");
	const [allowAuthenticatedLogin, setAllowAuthenticatedLogin] = useState(false);
	const [showAccountSwitcher, setAccountSwitcherVisibility] = useState(false);
	const [savedAccounts, setSavedAccounts] = useState<SavedLoginAccount[]>([]);
	const [switchingAccountId, setSwitchingAccountId] = useState<string | null>(null);
	const [pending2FAAccount, setPending2FAAccount] = useState<SavedLoginAccount | null>(null);
	const [isSubmitting2FA, setIsSubmitting2FA] = useState(false);
	const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const longPressTriggeredRef = useRef(false);
	const suppressNextClickRef = useRef(false);
	const { toast } = useToast();
	// Avatar components are now imported directly, no need for dynamic loading
	const avatarComponents = { Avatar, AvatarFallback, AvatarImage };
	const canShowAccountSwitcher = session?.user
		? canUseAccountSwitcher(session, savedAccounts)
		: false;

	useOutOfBoundsHitToggle(wrapperRef, [clicked, setMenuVisibility]);
	useOutOfBoundsHitToggle(wrapperRef, [showAccountSwitcher, setAccountSwitcherVisibility]);

	useEffect(() => {
		// Mark as mounted after hydration to prevent hydration mismatches
		setMounted(true);
	}, []);

	useEffect(() => {
		// Manage body overflow when menu is open/closed
		const root = document.body;
		if ((showMenu && clicked) || showAccountSwitcher) {
			root.style.overflow = "hidden";
		} else {
			root.style.overflow = "auto";
		}
		
		return () => {
			root.style.overflow = "auto";
		};
	}, [showMenu, clicked, showAccountSwitcher]);

	useEffect(() => {
		setMenuVisibility(false);
		setAccountSwitcherVisibility(false);
	}, [pathname, setAccountSwitcherVisibility, setMenuVisibility]);

	useEffect(() => {
		saveCurrentAccountIfEligible(session);
		setSavedAccounts(getSavedLoginAccounts());
	}, [session?.idToken, session?.user?.id]);

	useEffect(() => {
		return () => {
			if (longPressTimerRef.current) {
				clearTimeout(longPressTimerRef.current);
			}
		};
	}, []);

	const openLoginModal = () => {
		setAllowAuthenticatedLogin(false);
		setLoginReturnUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`);
		setMenuVisibility(false);
		setShowLoginModal(true);
	};

	const refreshSavedAccounts = () => {
		const accounts = getSavedLoginAccounts();
		setSavedAccounts(accounts);
		return accounts;
	};

	const openAccountSwitcher = () => {
		if (!canShowAccountSwitcher) return;

		saveCurrentAccountIfEligible(session);
		const accounts = refreshSavedAccounts();
		if (accounts.length === 0) {
			toast({
				title: "No saved accounts yet",
				description: "Save an admin or director account first.",
			});
			return;
		}

		setMenuVisibility(false);
		setAccountSwitcherVisibility(true);
	};

	const clearLongPressTimer = () => {
		if (longPressTimerRef.current) {
			clearTimeout(longPressTimerRef.current);
			longPressTimerRef.current = null;
		}
	};

	const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
		if (!canShowAccountSwitcher || event.button !== 0) return;

		clearLongPressTimer();
		longPressTriggeredRef.current = false;
		longPressTimerRef.current = setTimeout(() => {
			longPressTriggeredRef.current = true;
			suppressNextClickRef.current = true;
			openAccountSwitcher();
		}, ACCOUNT_SWITCHER_LONG_PRESS_MS);
	};

	const handlePointerEnd = () => {
		clearLongPressTimer();
	};

	const handleButtonClick = () => {
		if (suppressNextClickRef.current || longPressTriggeredRef.current) {
			suppressNextClickRef.current = false;
			longPressTriggeredRef.current = false;
			return;
		}

		setAccountSwitcherVisibility(false);
		setMenuVisibility((old) => !old);
	};

	const removeAccountFromSwitcher = (account: SavedLoginAccount) => {
		removeSavedLoginAccount(account.userId);
		refreshSavedAccounts();
		toast({
			title: "Account removed",
			description: `${getSavedAccountDisplayName(account)} was removed from this device.`,
		});
	};

	const finishSuccessfulSwitch = (account: SavedLoginAccount) => {
		moveSavedAccountToTop(account.userId);
		invalidateApiSessionCache();
		setSwitchingAccountId(null);
		reloadCurrentPage();
	};

	const handleSwitchResult = (result: AccountSwitchResult, account: SavedLoginAccount) => {
		if (result.status === "requires2FA") {
			setPending2FAAccount(account);
			setSwitchingAccountId(null);
			return;
		}

		if (result.status === "unauthorized") {
			markSavedLoginAccountSignedOut(account.userId);
			refreshSavedAccounts();
			setAllowAuthenticatedLogin(true);
			setLoginReturnUrl(getCurrentReturnUrl());
			setShowLoginModal(true);
			setSwitchingAccountId(null);
			toast({
				title: "Login required",
				description: `${getSavedAccountDisplayName(account)}'s saved session expired. Please log in again.`,
			});
			return;
		}

		if (result.status === "error") {
			throw new Error(result.message);
		}

		finishSuccessfulSwitch(account);
	};

	const switchToSavedAccount = async (account: SavedLoginAccount, isCurrent: boolean) => {
		setAccountSwitcherVisibility(false);
		if (isCurrent) return;

		if (isSavedLoginAccountSignedOut(account)) {
			saveCurrentAccountIfEligible(session);
			void parkSessionForAccountSwitcher(session);
			refreshSavedAccounts();
			setAllowAuthenticatedLogin(true);
			setLoginReturnUrl(getCurrentReturnUrl());
			setShowLoginModal(true);
			toast({
				title: "Login required",
				description: `Please log in again as ${getSavedAccountDisplayName(account)}.`,
			});
			return;
		}

		setSwitchingAccountId(account.userId);
		try {
			const result = await executeAccountSwitch(account, session);
			handleSwitchResult(result, account);
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not switch account",
				description: error instanceof Error ? error.message : "Please log in again.",
			});
			setSwitchingAccountId(null);
		}
	};

	const handleAccountSwitch2FA = async (twoFactorToken: string) => {
		if (!pending2FAAccount) return;

		setIsSubmitting2FA(true);
		try {
			const result = await executeAccountSwitch(pending2FAAccount, session, { twoFactorToken });
			if (result.status === "success") {
				const account = pending2FAAccount;
				setPending2FAAccount(null);
				finishSuccessfulSwitch(account);
				return;
			}
			if (result.status === "unauthorized") {
				throw new Error("Session expired. Please log in again.");
			}
			if (result.status === "error") {
				throw new Error(result.message);
			}
			throw new Error("Invalid authentication code. Please try again.");
		} finally {
			setIsSubmitting2FA(false);
		}
	};

	const addAccountFromSwitcher = async () => {
		const liveSession = await getSession();
		const activeSession = liveSession ?? session;
		await parkSessionForAccountSwitcher(activeSession);
		const savedCurrent = saveCurrentAccountIfEligible(activeSession);
		const accounts = refreshSavedAccounts();
		if (!savedCurrent && accounts.length === 0) {
			toast({
				variant: "destructive",
				title: "Could not save current account",
				description: "Please log out and log in again before adding another account.",
			});
			return;
		}
		if (accounts.length >= MAX_SAVED_LOGIN_ACCOUNTS) {
			toast({
				title: "Maximum 10 accounts reached",
				description: "Remove an account before adding another one.",
			});
			return;
		}

		setMenuVisibility(false);
		setAccountSwitcherVisibility(false);
		setAllowAuthenticatedLogin(true);
		setLoginReturnUrl("/");
		setShowLoginModal(true);
	};

	return (
		<div ref={wrapperRef} className="group relative">
			<button
				aria-haspopup="menu"
				aria-controls="nav-left-menu"
				aria-expanded={showMenu || showAccountSwitcher}
				aria-label="Menu"
				className="flex size-10 min-h-[44px] min-w-[44px] items-center justify-center"
				onPointerDown={handlePointerDown}
				onPointerUp={handlePointerEnd}
				onPointerCancel={handlePointerEnd}
				onPointerLeave={handlePointerEnd}
				onContextMenu={(event) => {
					if (canShowAccountSwitcher) event.preventDefault();
				}}
				onClick={handleButtonClick}
			>
				<ButtonContent expanded={clicked} session={session} avatarComponents={avatarComponents} />
			</button>
			{showAccountSwitcher && mounted && session?.user && (
				<div
					id="mobile-account-switcher-dropdown"
					className="fixed left-1/2 top-[calc(90px+1.5rem)] z-[60] w-[calc(100vw-2rem)] max-w-sm -translate-x-[45%]"
				>
					<div
						className="dropdown-glassmorphism flex w-full flex-col overflow-hidden rounded-2xl"
						style={{
							maxHeight: "calc(100lvh - 120px)",
							border: '1px solid var(--glass-border)',
							boxShadow: 'var(--glass-shadow)',
							background: 'var(--glass-background)',
						}}
					>
						<div className="flex items-center justify-between px-5 pb-2 pt-4">
							<p className="text-sm2 font-semibold text-foreground">Switch account</p>
							<span className="text-xxsm text-muted-foreground">
								{savedAccounts.length}/{MAX_SAVED_LOGIN_ACCOUNTS}
							</span>
						</div>
						<div className="muted-scrollbar min-h-0 overflow-y-auto">
							{savedAccounts.map((account) => {
								const isCurrent = String(account.userId) === String(session.user.id);
								const isSwitching = switchingAccountId === account.userId;

								return (
									<AccountSwitcherAccountRow
										key={account.userId}
										account={account}
										isCurrent={isCurrent}
										isSwitching={isSwitching}
										disabled={switchingAccountId !== null}
										onSelect={() => switchToSavedAccount(account, isCurrent)}
										onRemove={() => removeAccountFromSwitcher(account)}
									/>
								);
							})}
						</div>
						<button
							type="button"
							className="flex w-full items-center gap-3 border-t border-tertiary/25 px-5 py-4 text-left transition-colors hover:bg-accent/10"
							onClick={addAccountFromSwitcher}
						>
							<span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-tertiary/10 text-lg font-semibold text-tertiary">
								+
							</span>
							<span className="text-sm font-semibold text-foreground">
								{savedAccounts.length >= MAX_SAVED_LOGIN_ACCOUNTS ? "Maximum 10 accounts reached" : "Add account"}
							</span>
						</button>
					</div>
				</div>
			)}
			{showMenu && clicked && mounted && (
				<div
					id="mobile-menu-dropdown"
					className="fixed left-1/2 top-[calc(90px+1.5rem)] z-[60] w-[calc(100vw-2rem)] max-w-sm -translate-x-[45%] md:absolute md:left-auto md:right-0 md:top-[calc(100%+0.5rem)] md:w-auto md:max-w-none md:translate-x-0"
				>
					<MenuDrawer
						expanded={clicked}
						session={session}
						plan={plan}
						onLoginClick={openLoginModal}
					/>
				</div>
			)}
			<AuthDialog
				open={showLoginModal}
				onOpenChange={(open) => {
					setShowLoginModal(open);
					if (!open) setAllowAuthenticatedLogin(false);
				}}
				returnUrl={loginReturnUrl}
				allowAuthenticatedLogin={allowAuthenticatedLogin}
				onAuthenticated={async () => {
					setShowLoginModal(false);
					if (allowAuthenticatedLogin) {
						const liveSession = await getSession();
						saveCurrentAccountIfEligible(liveSession);
						invalidateApiSessionCache();
						reloadCurrentPage();
					}
				}}
			/>
			<AccountSwitcherTwoFactorDialog
				open={!!pending2FAAccount}
				account={pending2FAAccount}
				isSubmitting={isSubmitting2FA}
				onOpenChange={(open) => {
					if (!open) setPending2FAAccount(null);
				}}
				onSubmit={handleAccountSwitch2FA}
			/>
		</div>
	);
}

type ButtonContentProps = { expanded: boolean; session: Session | null; avatarComponents: { Avatar: any; AvatarFallback: any; AvatarImage: any } | null };
function ButtonContent({ expanded, session, avatarComponents }: ButtonContentProps) {
	const userInitials = useMemo(
		() => getInitials(session?.user?.firstName, session?.user?.lastName),
		[session],
	);

	if (expanded) {
		return <X className="text-secondary" />;
	}

	if (session?.user && avatarComponents) {
		const { Avatar, AvatarFallback, AvatarImage } = avatarComponents;
		const hasImage =
			typeof session.user.avatar === "string" && session.user.avatar.trim().length > 0;
		return (
			<div
				id="nav-profile-avatar-mobile"
				className="relative inline-flex size-10 items-center justify-center overflow-hidden rounded-full border-2 border-tertiary text-base font-bold text-white"
				style={{
					backgroundColor: "#07A8BC",
					background: "#07A8BC",
					opacity: 1,
				}}
			>
				{hasImage ? (
					<Avatar className="size-full bg-transparent">
						<AvatarImage src={session.user.avatar} alt="avatar" className="size-full" />
						<AvatarFallback
							id="nav-profile-avatar-fallback"
							className="size-full text-base font-bold"
							style={{
								backgroundColor: "#07A8BC",
								background: "#07A8BC",
								color: "#FFFFFF",
								opacity: 1,
							}}
						>
							{userInitials}
						</AvatarFallback>
					</Avatar>
				) : (
					<span
						style={{ color: "#FFFFFF", fontWeight: "bold", opacity: 1 }}
					>
						{userInitials}
					</span>
				)}
			</div>
		);
	}

	if (session?.user) {
		return (
			<div
				id="nav-profile-avatar-mobile"
				className="inline-flex size-10 items-center justify-center rounded-full border-2 border-tertiary text-base font-bold text-white"
				style={{
					backgroundColor: "#07A8BC",
					background: "#07A8BC",
					opacity: 1,
				}}
			>
				<span style={{ color: "#FFFFFF", fontWeight: "bold", opacity: 1 }}>{userInitials}</span>
			</div>
		);
	}

	return <MenuIcon className="size-5 text-foreground md:size-6" aria-hidden />;
}

type MenuDrawerProps = {
	expanded: boolean;
	session: Session | null;
	plan: Plan | null;
	onLoginClick: () => void;
};
function MenuDrawer({ expanded, session, plan, onLoginClick }: MenuDrawerProps) {
	return (
		<div
			className={cn(
				"mobile-menu-glassmorphism flex w-full min-w-72 max-h-[calc(100lvh-120px)] flex-col py-6 px-4 gap-6 overflow-y-auto rounded-2xl",
			)}
			style={{
				border: '1px solid var(--glass-border)',
				boxShadow: 'var(--glass-shadow)',
				background: 'var(--glass-background)',
			}}
			// Note(Curstantine): stop bubbling click events on this div to prevent the menu-drawer from closing
			// unless of course if it's an Link element.
			onClick={(e) => {
				const { tagName } = e.target as Element;
				if (tagName !== "A") e.stopPropagation();
			}}
		>
			<div className="inline-flex flex-col gap-6 text-lg font-semibold text-secondary">
				<Link href="/">Home</Link>
				<AuthenticatedItems session={session} />
				<Link href="/subscription/monthly">Plans</Link>
				<Link href="/hosting/monthly">Hosting</Link>
				<Link href="/faqs">FAQs</Link>
			</div>

			<DrawerPlan session={session} plan={plan} />

			{!session && (
				<div className="col-span-full inline-flex justify-between text-lg font-semibold text-secondary">
					<TrackedNativeButton
						ctaId="nav_login"
						ctaName="Log In"
						ctaLocation="mobile_nav"
						onClick={onLoginClick}
					>
						Log In
					</TrackedNativeButton>
					<TrackedCtaLink
						href="/signup"
						ctaId="nav_signup"
						ctaName="Sign Up"
						ctaLocation="mobile_nav"
					>
						Sign Up
					</TrackedCtaLink>
				</div>
			)}

			<div className="flex-1" />

			<div className="grid grid-cols-2 gap-2 text-accent">
				<div id="mobile-app-button-apple">
					<MobileAppButton
						size="xs"
						background="dark"
						href={APPLE_APP_STORE_LINK}
						store="Apple Store"
						labelTop="Download on"
						iconLight={AppleIconLight}
						iconDark={AppleIconDark}
						className="px-2 [&>div:first-child]:pl-2 [&>div:last-child]:-ml-1"
						ctaLocation="nav_mobile_menu"
					/>
				</div>
				<div id="mobile-app-button-google">
					<MobileAppButton
						size="xs"
						background="dark"
						href={GOOGLE_APP_STORE_LINK}
						store="Google Play"
						labelTop="Get it on"
						iconLight={GoogleIconLight}
						iconDark={GoogleIconDark}
						className="px-2 [&>div:first-child]:pl-2 [&>div:last-child]:-ml-1"
						ctaLocation="nav_mobile_menu"
					/>
				</div>
			</div>

			<div
				id="mobile-menu-legal-links"
				className="mx-auto grid w-full max-w-[17rem] grid-cols-[1fr_auto_1fr] items-center gap-4 text-[0.6875rem]"
			>
				<Link href="/compliance/tos" className="justify-self-end text-right">
					Terms of Service
				</Link>
				<span
					aria-hidden="true"
					className="h-3.5 w-px"
				/>
				<Link href="/compliance/privacy" className="justify-self-start text-left">
					Privacy Policy
				</Link>
			</div>
		</div>
	);
}

function DrawerPlan({ session, plan }: Pick<MenuDrawerProps, "session" | "plan">) {
	if (session === null || plan === null) return <></>;

	const isInFreePlan = !isUserPremium(session.user.plan.duration);
	const currentPlanStyle = {
		"--current-plan-button-bg": plan.colors.bgColor,
		"--current-plan-button-fg": plan.colors.fontColor,
		backgroundColor: plan.colors.bgColor,
		background: plan.colors.bgColor,
		color: plan.colors.fontColor,
	} as CSSProperties;
	const upgradeButtonStyle = {
		"--upgrade-button-bg": "rgb(26, 26, 26)",
		"--upgrade-button-fg": "rgb(63, 224, 208)",
		backgroundColor: "rgb(26, 26, 26)",
		background: "rgb(26, 26, 26)",
		color: "rgb(63, 224, 208)",
	} as CSSProperties;

	return (
		<div className="inline-flex flex-col">
			<span className="pb-2 text-sm font-semibold">Your current plan</span>
			<button 
				id="current-plan-button"
				type="button"
				style={{ 
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					borderRadius: '0.5rem',
					padding: '0.5rem 1rem',
					height: '2.5rem',
					fontSize: '0.875rem',
					fontWeight: '600',
					boxShadow: '0 4px 12px rgba(63, 224, 208, 0.3), 0 2px 4px rgba(63, 224, 208, 0.2)',
					opacity: 1,
					border: 'none',
					cursor: 'pointer',
					...currentPlanStyle,
				}}
			>
				<span style={{ color: plan.colors.fontColor }}>{toHeaderCase(plan.name)}</span>
			</button>
			{isInFreePlan && (
				<>
					<Separator
						id="mobile-menu-plan-separator"
						className="mx-auto my-4 w-24"
					/>
					<button 
						id="upgrade-premium-button"
						type="button"
						style={{ 
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexDirection: 'column',
							borderRadius: '0.5rem',
							padding: '0.5rem 1rem',
							height: '3.5rem',
							fontSize: '0.875rem',
							fontWeight: '600',
							boxShadow: '0 4px 12px rgba(63, 224, 208, 0.3), 0 2px 4px rgba(63, 224, 208, 0.2)',
							opacity: 1,
							border: 'none',
							cursor: 'pointer',
							...upgradeButtonStyle,
						}}
					>
						<Link href="/subscription/monthly" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none' }}>
							<span style={{ color: 'rgb(63, 224, 208)', fontWeight: 'bold' }}>Upgrade to premium</span>
							<span style={{ display: 'block', fontSize: '0.75rem', fontWeight: '300', color: 'rgb(63, 224, 208)' }}>
								Get 10% off on all orders
							</span>
						</Link>
					</button>
				</>
			)}
		</div>
	);
}

function AuthenticatedItems({ session }: { session: Session | null }) {
	if (!session?.user?.id) return null;

	const isAdmin = isUserAdmin(session.user.role);
	const is2FAEnabled = (session.user as { is2FAEnabled?: boolean }).is2FAEnabled;
	const showAdminPanel = isAdmin && is2FAEnabled === true;

	const profileDestinations: [string, string][] = [
		["/profile/inbox", "Support"],
		["/profile/order-history", "Orders"],
		["/profile/saved", "Saved"],
		["/profile/preferences", "Preferences"],
		["/profile/settings", "Settings"],
		["/profile/affiliates", "Affiliates"],
		["/profile/automation", "Automation"],
		...(showAdminPanel ? ([["/admin/dashboard", "Admin Panel"]] as [string, string][]) : []),
		["/profile/logout", "Logout"],
	];

	return <CollapsibleTargets label="Profile" destinations={profileDestinations} />;
}

type CollapsibleTargetsProps = {
	label: string;
	destinations: [string, string][];
};

function CollapsibleTargets({ label, destinations }: CollapsibleTargetsProps) {
	const [expanded, setExpanded] = useState(false);

	return (
		<Collapsible open={expanded} onOpenChange={setExpanded} className="space-y-2">
			<CollapsibleTrigger asChild>
				<button className="inline-flex items-center gap-2">
					{label}
					<ChevronDown
						className={cn(
							"w-6 rotate-0 transform-gpu text-secondary transition-transform duration-300",
							expanded && "rotate-180",
						)}
					/>
				</button>
			</CollapsibleTrigger>

			<CollapsibleContent
				className={clsx(
					"grid grid-cols-1 gap-2 overflow-hidden pl-8 text-lg font-normal",
					expanded ? "animate-collapsible-down" : "animate-collapsible-up",
				)}
			>
				{destinations.map(([href, label]) => (
					<Link key={href} href={href}>
						{label}
					</Link>
				))}
			</CollapsibleContent>
		</Collapsible>
	);
}
